import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import { color, typography } from 'styled-system';
import { Field, Form } from 'formik';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import Select from 'react-select';

import Text from '../../../../../shared/_TBComponents/Text';
import useReseller from '../../hooks/useReseller';
import useResellerPrograms from '../../hooks/useResellerPrograms';

import 'react-datepicker/dist/react-datepicker.css';

const PROGRAM_SOURCE_OPTIONS = [
  {
    label: 'Calendar',
    value: 'calendar',
  },
  {
    label: 'Program Library',
    value: 'savedWorkout',
  },
];

const AnotherWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CheckboxWrapper = styled('div')`
  padding-top: 12px;
`;

const FieldError = styled('div')`
  color: ${(props) => props.theme.colors.error || '#ff6600'};
  font-size: 14px;
  margin-top: 5px;
`;

const FieldErrorForOffsetDate = styled('div')`
  color: ${(props) => props.theme.colors.error || '#ff6600'};
  font-size: 14px;
  margin-bottom: 4px;
  margin-left: 2px;
`;

const FirstWorkoutContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FormGroup = styled('div')`
  width: 100%;

  label {
    width: 100%;
  }

  button {
    margin-bottom: 20px;
  }

  .easy-join {
    margin-bottom: 15px;
  }

  .caveat-text {
    font-size: 11px;
    margin-top: 5px;
  }

  @media screen and (max-width: 600px) {
    a {
      font-size: 11px;
    }
  }

  .form-control {
    width: 100%;
    min-height: unset;
  }

  .first-stage-select {
    width: 100%;
  }

  .checkbox-label {
    display: flex;
    margin-top: 10px;
  }

  .checkbox {
    margin-right: 10px;
    margin-top: 3px;
  }
`;

const InnerFolderFormWrapper = styled('div')`
  align-items: center;
  display: flex;
  justify-content: left;
  margin-top: 5px;
  width: 80%;

  .formik-form {
    width: 100%;
  }
`;

const ModalWrapper = styled('div')`
  display: flex;
  width: 100%;

  .modal-button {
    align-items: center;
    display: flex;
  }
`;

const OuterFolderFormWrapper = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;

  .divider-line {
    border-top: 2px solid rgb(232, 232, 232);
    display: flex;
    margin-bottom: 10px;
    margin-top: 5px;
    width: calc(100% + 80px);
  }
`;

const SubText = styled('div')`
  color: ${(props) => props.theme.colors.text || '#444444'};
  font-family: "Nunito Sans";
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  ${typography}
  ${color}
`;

const TextBoxContainer = styled('div')`
  border-color: ${(props) => props.borderColor};
  border-radius: 4px;
  border-style: solid;
  border-width: ${(props) => props.borderWidth};
  cursor: pointer;
  display: flex;
  flex-basis: 100%;
  flex-direction: column;
  height: 165px;
  margin-left: ${(props) => props.marginLeft};
  margin-right: ${(props) => props.marginRight};
`;

const TextBoxContent = styled('div')`
  font-size: 16px;
  font-weight: 300;
  margin-left: 6px;
  margin-right: 6px;
`;

const TextBoxContentContainer = styled('div')`
  align-items: center;
  display: flex;
  text-align: center;
`;

const TextBoxOuterWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const TextBoxTitle = styled('div')`
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 12px;
  margin-top: 15px;
  text-transform: uppercase;
`;

const TextBoxTitleContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  text-align: center;
`;

const TitlePlusErrorWrapper = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const WarningError = styled('div')`
  font-size: 12px;
  margin-bottom: 5px;
  margin-top: 5px;
`;

const CreateProgramSlide2 = ({
  formProps,
  page2Errors,
  programSource,
  programType,
  savedWorkoutError,
  selectedCalendar,
  selectedDate,
  selectedGroups,
  selectedSavedWorkouts,
  setProgramSource,
  setProgramType,
  setSavedWorkoutError,
  setSelectedCalendar,
  setSelectedDate,
  setSelectedGroups,
  setSelectedSavedWorkouts,
}) => {
  const theme = useTheme();

  const {
    calendars, currentUser, savedWorkouts, userGroups,
  } = useReseller();

  const {
    firstWorkout,
    firstWorkoutError,
    handleFetchFirstWorkout,
  } = useResellerPrograms();

  const [flattenedCalendars, setFlattenedCalendars] = useState([]);
  const lastFetchedCalendarIdRef = useRef(null);

  const isCalendarSelected = useMemo(
    () => Object.keys(selectedCalendar || {}).length > 0,
    [selectedCalendar],
  );

  useEffect(() => {
    if (calendars && calendars.length > 0) {
      const flattened = calendars.flatMap((calendar) => [
        calendar,
        ...(calendar.childCalendars || []),
      ]);
      setFlattenedCalendars(flattened);
    }
  }, [calendars]);

  useEffect(() => {
    const accountCode = currentUser?.accountCode;
    const calendarId = selectedCalendar?.id;

    if (!isCalendarSelected || !calendarId) {
      lastFetchedCalendarIdRef.current = null;
      return;
    }

    if (accountCode && lastFetchedCalendarIdRef.current !== calendarId) {
      lastFetchedCalendarIdRef.current = calendarId;
      handleFetchFirstWorkout({
        accountCode,
        calendarId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.accountCode, isCalendarSelected, selectedCalendar?.id]);

  useEffect(() => {
    if (firstWorkout && !firstWorkoutError && !selectedDate) {
      const date = moment(firstWorkout.date);
      setSelectedDate(date);
      formProps.setFieldValue('calendarOffsetDate', firstWorkout.date);
    }
  }, [
    firstWorkout,
    firstWorkoutError,
    formProps,
    selectedDate,
    setSelectedDate,
  ]);

  const handleProgramSourceChange = (option) => {
    setProgramSource(option);
    setSelectedCalendar('');
    setSelectedSavedWorkouts([]);

    if (option.value === 'calendar') {
      formProps.setFieldValue('savedWorkoutIds', '');
      formProps.setFieldValue('billingFrequency', 1);
    } else {
      formProps.setFieldValue('billingFrequency', 0);
      formProps.setFieldValue('calendarId', 0);
    }
  };

  const handleCalendarChange = (options) => {
    if (options) {
      formProps.setFieldTouched('calendarId', true);
      formProps.setFieldValue('calendarId', options.id);
      setSelectedCalendar(options);
      setSelectedSavedWorkouts([]);
      formProps.setFieldValue('savedWorkoutIds', '');
      formProps.setFieldValue('billingFrequency', 1);
    } else {
      formProps.setFieldValue('calendarId', 0);
      formProps.setFieldValue('calendarOffsetDate', '');
      formProps.setFieldValue('programType', null);
      formProps.setFieldValue('mondayStart', null);
      formProps.setFieldTouched('calendarId', true);
      setProgramType(null);
      setSelectedDate(null);
      setSelectedCalendar('');
    }
  };

  const handleProgramTypeClick = (selectedProgramType) => {
    formProps.setFieldValue('programType', selectedProgramType);
    setProgramType(selectedProgramType);

    if (selectedProgramType === 'ONGOING') {
      formProps.setFieldValue('calendarOffsetDate', '');
      formProps.setFieldValue('mondayStart', null);
    } else if (selectedDate) {
      formProps.setFieldValue(
        'calendarOffsetDate',
        moment(selectedDate).format('YYYY-MM-DD'),
      );
    }
  };

  const handleDateChange = (date) => {
    if (date) {
      const newDate = moment(date);
      const finalDateString = newDate.format('YYYY-MM-DD');
      formProps.setFieldValue('calendarOffsetDate', finalDateString);
    } else {
      formProps.setFieldValue('calendarOffsetDate', '');
    }
    setSelectedDate(date);
  };

  const handleSavedWorkoutsChange = (options) => {
    const savedWorkoutIds = options?.map((workout) => workout.id) || [];
    setSelectedSavedWorkouts(options || []);

    formProps.setFieldValue('savedWorkoutIds', savedWorkoutIds);

    if (savedWorkoutIds.length > 0 && savedWorkoutError) {
      setSavedWorkoutError('');
    }

    if (savedWorkoutIds.length > 0) {
      formProps.setFieldValue('billingFrequency', 0);
    } else {
      formProps.setFieldValue('billingFrequency', '');
    }
  };

  const handleGroupsChange = (options) => {
    const groupIds = options?.map((group) => group.id) || [];
    setSelectedGroups(options || []);
    formProps.setFieldValue('groups', groupIds);
  };

  const handleMondayStartChange = () => {
    formProps.setFieldValue('mondayStart', !formProps.values.mondayStart);
  };

  const handleAutoStartChange = () => {
    formProps.setFieldValue(
      'autoStartProgram',
      !formProps.values.autoStartProgram,
    );
  };

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      paddingLeft: state.data.parentId ? 35 : 15,
    }),
  };

  const getBorderColor = (isSelected) => (isSelected ? theme.colors.green : theme.colors.border || 'hsl(0,0%,80%)');

  const getBorderWidth = (isSelected) => (isSelected ? '2px' : '1px');

  const formGroupTextProps = { marginBottom: '5px', marginTop: '10px' };

  return (
    <ModalWrapper>
      <AnotherWrapper>
        <OuterFolderFormWrapper>
          <InnerFolderFormWrapper>
            <Form className='formik-form'>
              <FormGroup>
                <TitlePlusErrorWrapper>
                  <Text styledWrapperProps={formGroupTextProps}>
                    <strong>Program Source</strong>
                  </Text>
                </TitlePlusErrorWrapper>
                <Select
                  className='first-stage-select'
                  defaultValue={programSource}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  options={PROGRAM_SOURCE_OPTIONS}
                  styles={customStyles}
                  onChange={handleProgramSourceChange}
                />
              </FormGroup>
              {programSource.value === 'calendar' ? (
                <>
                  <FormGroup>
                    <TitlePlusErrorWrapper>
                      <Text
                        styledTextProps={{ fontWeight: 700 }}
                        styledWrapperProps={formGroupTextProps}
                      >
                        Calendar Assignment
                      </Text>
                    </TitlePlusErrorWrapper>
                    <Select
                      className='first-stage-select'
                      defaultValue={selectedCalendar}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      isClearable={isCalendarSelected}
                      isOptionSelected={(option) => formProps.values.calendarId === option.id}
                      isSearchable
                      options={flattenedCalendars}
                      styles={customStyles}
                      onChange={handleCalendarChange}
                    />
                  </FormGroup>
                  {isCalendarSelected && (
                    <FormGroup>
                      <TitlePlusErrorWrapper>
                        <Text
                          styledTextProps={{ fontWeight: 700 }}
                          styledWrapperProps={formGroupTextProps}
                        >
                          Program Type
                        </Text>
                        {formProps.errors.programType && page2Errors && (
                          <FieldError className='error-text'>
                            {formProps.errors.programType}
                          </FieldError>
                        )}
                      </TitlePlusErrorWrapper>
                      <TextBoxOuterWrapper>
                        <TextBoxContainer
                          borderColor={getBorderColor(
                            programType === 'PRE-BUILT',
                          )}
                          borderWidth={getBorderWidth(
                            programType === 'PRE-BUILT',
                          )}
                          marginRight='10px'
                          onClick={() => handleProgramTypeClick('PRE-BUILT')}
                        >
                          <TextBoxTitleContainer>
                            <TextBoxTitle>Pre-Built Program</TextBoxTitle>
                          </TextBoxTitleContainer>
                          <TextBoxContentContainer>
                            <TextBoxContent>
                              I have built workouts on this calendar already.
                            </TextBoxContent>
                          </TextBoxContentContainer>
                        </TextBoxContainer>
                        <TextBoxContainer
                          borderColor={getBorderColor(
                            programType === 'ONGOING',
                          )}
                          borderWidth={getBorderWidth(
                            programType === 'ONGOING',
                          )}
                          marginLeft='10px'
                          onClick={() => handleProgramTypeClick('ONGOING')}
                        >
                          <TextBoxTitleContainer>
                            <TextBoxTitle>Ongoing Program</TextBoxTitle>
                          </TextBoxTitleContainer>
                          <TextBoxContentContainer>
                            <TextBoxContent>
                              I will be adding workouts to this calendar on an
                              ongoing basis.
                            </TextBoxContent>
                          </TextBoxContentContainer>
                        </TextBoxContainer>
                      </TextBoxOuterWrapper>
                    </FormGroup>
                  )}
                  {isCalendarSelected && programType === 'ONGOING' && (
                    <FormGroup>
                      <Text
                        styledTextProps={{ fontSize: '16px' }}
                        styledWrapperProps={{ marginTop: '15px' }}
                      >
                        Subscriber will be placed in calendar on day of
                        registration.
                      </Text>
                    </FormGroup>
                  )}
                  {isCalendarSelected && programType === 'PRE-BUILT' && (
                    <FormGroup>
                      <FirstWorkoutContainer>
                        <TitlePlusErrorWrapper>
                          <SubText className='toggle-text' fontSize={10}>
                            First workout is on:
                            {' '}
                          </SubText>
                          {formProps.errors.calendarOffsetDate
                            && page2Errors && (
                              <FieldErrorForOffsetDate className='error-text'>
                                {formProps.errors.calendarOffsetDate}
                              </FieldErrorForOffsetDate>
                          )}
                        </TitlePlusErrorWrapper>
                        <DatePicker
                          className='form-control'
                          selected={
                            selectedDate && isCalendarSelected
                              ? Date.parse(selectedDate)
                              : null
                          }
                          onChange={handleDateChange}
                        />
                      </FirstWorkoutContainer>
                    </FormGroup>
                  )}
                  {isCalendarSelected && programType === 'PRE-BUILT' && (
                    <FormGroup className='d-flex'>
                      <CheckboxWrapper>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label
                          className='checkbox-label'
                          htmlFor='monday-start-field'
                        >
                          <Field
                            className='checkbox'
                            checked={formProps.values.mondayStart}
                            id='monday-start-field'
                            name='monday-start'
                            type='checkbox'
                            onChange={handleMondayStartChange}
                          />
                          <Text styledTextProps={{ fontSize: '14px' }}>
                            Subscriber should start workouts on a Monday
                          </Text>
                        </label>
                      </CheckboxWrapper>
                    </FormGroup>
                  )}
                  {isCalendarSelected
                    && programType === 'PRE-BUILT'
                    && formProps.values.mondayStart && (
                      <FormGroup className='d-flex'>
                        <Text styledTextProps={{ fontSize: '14px' }}>
                          *We recommend scheduling 7 days of
                          &ldquo;lead-in&rdquo; workouts prior to the first
                          workout date so the customer can see workouts
                          immediately after purchase
                        </Text>
                      </FormGroup>
                  )}
                </>
              ) : (
                <>
                  {savedWorkouts?.length > 0 && (
                    <FormGroup>
                      <Text
                        styledTextProps={{ fontWeight: 700 }}
                        styledWrapperProps={formGroupTextProps}
                      >
                        Assign Saved Workout when Purchased
                      </Text>
                      {isCalendarSelected && (
                        <WarningError className='error-text'>
                          Saved workouts cannot be assigned with a calendar
                        </WarningError>
                      )}
                      {selectedSavedWorkouts?.length > 0 && (
                        <WarningError className='error-text'>
                          Selecting saved workouts will require this program to
                          be a one-time charge program
                        </WarningError>
                      )}
                      {savedWorkoutError && (
                        <FieldError className='error-text'>
                          {savedWorkoutError}
                        </FieldError>
                      )}
                      <Select
                        getOptionLabel={(option) => option.programName}
                        getOptionValue={(option) => option.id}
                        isClearable
                        isDisabled={isCalendarSelected}
                        isMulti
                        options={savedWorkouts}
                        value={selectedSavedWorkouts}
                        onChange={handleSavedWorkoutsChange}
                      />
                    </FormGroup>
                  )}
                  {selectedSavedWorkouts?.length > 0 && !isCalendarSelected && (
                    <FormGroup className='d-flex'>
                      <CheckboxWrapper>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label
                          className='checkbox-label'
                          htmlFor='agreement-field'
                        >
                          <Field
                            className='checkbox'
                            checked={formProps.values.autoStartProgram}
                            id='agreement-field'
                            name='autoStartProgram'
                            type='checkbox'
                            onChange={handleAutoStartChange}
                          />
                          <Text styledTextProps={{ fontSize: '14px' }}>
                            Automatically start associated programs for athletes
                            when program is purchased.
                          </Text>
                        </label>
                      </CheckboxWrapper>
                    </FormGroup>
                  )}
                </>
              )}
              <FormGroup>
                <Text
                  styledTextProps={{ fontWeight: 700 }}
                  styledWrapperProps={formGroupTextProps}
                >
                  Group Assignment
                </Text>
                <Select
                  defaultValue={selectedGroups}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  isClearable
                  isMulti
                  isOptionSelected={(group) => formProps.values.group === group}
                  options={userGroups}
                  onChange={handleGroupsChange}
                />
              </FormGroup>
            </Form>
          </InnerFolderFormWrapper>
        </OuterFolderFormWrapper>
      </AnotherWrapper>
    </ModalWrapper>
  );
};

CreateProgramSlide2.propTypes = {
  formProps: PropTypes.instanceOf(Object).isRequired,
  page2Errors: PropTypes.bool.isRequired,
  programSource: PropTypes.instanceOf(Object).isRequired,
  programType: PropTypes.string,
  savedWorkoutError: PropTypes.string.isRequired,
  selectedCalendar: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.string,
  ]).isRequired,
  selectedDate: PropTypes.instanceOf(Object),
  selectedGroups: PropTypes.instanceOf(Array).isRequired,
  selectedSavedWorkouts: PropTypes.instanceOf(Array).isRequired,
  setProgramSource: PropTypes.func.isRequired,
  setProgramType: PropTypes.func.isRequired,
  setSavedWorkoutError: PropTypes.func.isRequired,
  setSelectedCalendar: PropTypes.func.isRequired,
  setSelectedDate: PropTypes.func.isRequired,
  setSelectedGroups: PropTypes.func.isRequired,
  setSelectedSavedWorkouts: PropTypes.func.isRequired,
};

CreateProgramSlide2.defaultProps = {
  programType: null,
  selectedDate: undefined,
};

export default CreateProgramSlide2;
